import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, RefreshCw, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

function formatLoginAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatTimeLeft(seconds) {
  if (seconds == null) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Live view of who is logged in right now (Redis-backed sessions). One row per
// USER, expandable to each independent session/device — every session keeps its
// OWN timer (logging in on a new device does NOT reset the others).
function ActiveSessionsPanel() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({}); // user_id -> bool

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/superadmin/active-sessions`);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'Superadmin access only — log in as a superadmin to view active sessions.'
          : (err?.response?.data?.detail || 'Failed to load active sessions.')
      );
      toast.error('Could not load active sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 30000);
    return () => clearInterval(t);
  }, [fetchSessions]);

  // Group by user, keeping each session intact so per-session timers stay honest.
  const byUser = {};
  for (const s of sessions) {
    const g = byUser[s.user_id] || { user_id: s.user_id, email: s.email, sessions: [] };
    g.sessions.push(s);
    byUser[s.user_id] = g;
  }
  const users = Object.values(byUser).map((u) => {
    const ordered = [...u.sessions].sort((a, b) => new Date(b.login_at || 0) - new Date(a.login_at || 0));
    const ttls = u.sessions.map((s) => s.expires_in_seconds).filter((v) => v != null);
    return {
      ...u,
      sessions: ordered,
      count: u.sessions.length,
      latest: ordered[0]?.login_at || null,
      // SOONEST-expiring session (not the max) so the summary never overstates.
      soonest: ttls.length ? Math.min(...ttls) : null,
    };
  }).sort((a, b) => new Date(b.latest || 0) - new Date(a.latest || 0));

  const toggle = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Everyone currently logged in. Each session keeps its own timer — expand a row to see every device.
        </p>
        <button
          onClick={fetchSessions}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
          <CardTitle className="text-xl flex items-center justify-between">
            <span className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /> Logged-in Users</span>
            <Badge className="bg-blue-600">{users.length} active</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {error ? (
            <div className="text-center py-10 text-red-600">{error}</div>
          ) : loading && sessions.length === 0 ? (
            <div className="text-center py-10 text-gray-400">Loading…</div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-gray-400">No one is logged in right now.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Most recent login</th>
                    <th className="py-2 pr-4">Sessions</th>
                    <th className="py-2 pr-4">Next to expire</th>
                    <th className="py-2 pr-4">User ID</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const open = !!expanded[u.user_id];
                    const multi = u.count > 1;
                    return (
                      <React.Fragment key={u.user_id}>
                        <tr
                          className={`border-b last:border-0 ${multi ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                          onClick={() => multi && toggle(u.user_id)}
                        >
                          <td className="py-2 pr-4 font-medium">
                            <span className="flex items-center gap-1">
                              {multi
                                ? (open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />)
                                : <span className="w-4 h-4 inline-block" />}
                              {u.email || '(unknown)'}
                            </span>
                          </td>
                          <td className="py-2 pr-4 text-gray-600">{formatLoginAt(u.latest)}</td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline" className="w-fit">
                              {u.count} {u.count === 1 ? 'device' : 'devices'}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4">
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <Clock className="w-3 h-3" />
                              {formatTimeLeft(u.soonest)}
                            </Badge>
                          </td>
                          <td className="py-2 pr-4 text-gray-400 font-mono text-xs">{u.user_id}</td>
                        </tr>
                        {open && u.sessions.map((s, idx) => (
                          <tr key={s.jti || `${u.user_id}-${idx}`} className="border-b last:border-0 bg-gray-50/60">
                            <td className="py-1.5 pr-4 pl-9 text-gray-500">↳ device {idx + 1}</td>
                            <td className="py-1.5 pr-4 text-gray-600">{formatLoginAt(s.login_at)}</td>
                            <td className="py-1.5 pr-4 text-gray-400 text-xs">this session</td>
                            <td className="py-1.5 pr-4">
                              <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                <Clock className="w-3 h-3" />
                                {formatTimeLeft(s.expires_in_seconds)}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-4 text-gray-300 font-mono text-xs">{(s.jti || '').slice(0, 8)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ActiveSessionsPanel;
